export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

type Json = Record<string, unknown> | unknown[] | null;

export async function api<T>(path: string, init: { method?: string; body?: Json; signal?: AbortSignal; headers?: Record<string, string> } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      'x-hearth-request': '1',
      ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    signal: init.signal,
  });
  return read<T>(res);
}

/** Fields and files as multipart/form-data (empty ones left out); the browser writes the content type and boundary. */
export async function sendForm<T>(path: string, fields: Record<string, string | Blob | null | undefined>, method = 'POST'): Promise<T> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== null && v !== '') form.set(k, v);
  const res = await fetch(path, { method, credentials: 'same-origin', headers: { accept: 'application/json', 'x-hearth-request': '1' }, body: form });
  return read<T>(res);
}

/** One file as the multipart field `file`. */
export const upload = <T>(path: string, file: File, method = 'PUT') => sendForm<T>(path, { file }, method);

async function read<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : null;
  if (!res.ok) {
    throw new ApiError(res.status, String(data?.code ?? 'error'), String(data?.title ?? data?.message ?? `Request failed (${res.status})`), data?.details);
  }
  return data as T;
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
export const post = <T>(path: string, body: Json = {}) => api<T>(path, { method: 'POST', body });
export const put = <T>(path: string, body: Json = {}) => api<T>(path, { method: 'PUT', body });
export const patch = <T>(path: string, body: Json = {}) => api<T>(path, { method: 'PATCH', body });

/**
 * A person's photo thumbnail, always served by Hearth itself. `version` (the photo's file id, when the
 * data has it) lets the browser keep the picture for a week; without it, for an hour.
 */
export function photoUrl(employeeId: string, opts: { size?: 'sm' | 'lg'; version?: string | null } = {}) {
  return `/api/people/${employeeId}/photo${qs({ s: opts.size === 'lg' ? 'lg' : undefined, v: opts.version ? opts.version.slice(0, 8) : undefined })}`;
}

export function qs(params: Record<string, string | number | boolean | null | undefined>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}
